import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const steps = [
  { 
    number: "01", 
    title: "Husk Collection", 
    desc: "Fresh coconut husks sourced from local farmers and plantations across Andhra Pradesh, ensuring the highest quality raw materials.",
    highlight: "100% Local Sourcing"
  },
  { 
    number: "02", 
    title: "Fiber Extraction", 
    desc: "State-of-the-art decorticating machines separate coir fiber from the husk with precision, maximizing yield and quality.",
    highlight: "Modern Technology"
  },
  { 
    number: "03", 
    title: "Processing", 
    desc: "Thorough washing, drying, and grading processes to meet specific quality standards and customer requirements.",
    highlight: "Quality Assured"
  },
  { 
    number: "04", 
    title: "Quality Check", 
    desc: "Rigorous testing for moisture content, pH levels, EC values, and fiber length to ensure international standards.",
    highlight: "Lab Tested"
  },
  { 
    number: "05", 
    title: "Packaging", 
    desc: "Carefully compressed and packaged according to customer specifications, ready for domestic and international shipping.",
    highlight: "Export Ready"
  },
];

export default function Process() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-[#F9FAFB] overflow-hidden relative" data-testid="process-section">
      {/* Background decorations */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-forest/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-coir/5 rounded-full blur-3xl -translate-y-1/2" />
      
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gradient-to-r from-transparent to-forest" />
            <span className="text-forest font-semibold text-sm uppercase tracking-[0.2em] font-sans">Our Process</span>
            <div className="w-12 h-[2px] bg-gradient-to-l from-transparent to-forest" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-navy mb-6 tracking-tight">
            From <span className="text-forest">Coconut</span> to <span className="text-coir">Product</span>
          </h2>
          <p className="text-slate-500 text-lg font-sans leading-relaxed">
            Our meticulous 5-step manufacturing process ensures the highest quality in every product.
          </p>
        </div>

        {/* Desktop Process Viewer */}
        <div className="hidden lg:block">
          {/* Progress Bar */}
          <div className="relative mb-16">
            {/* Background line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-slate-200 -translate-y-1/2 rounded-full" />
            {/* Progress line */}
            <div 
              className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-forest to-coir -translate-y-1/2 rounded-full transition-all duration-700" 
              style={{ width: `${(active / (steps.length - 1)) * 100}%` }} 
            />
            
            {/* Step Buttons */}
            <div className="relative flex justify-between">
              {steps.map((step, i) => (
                <button 
                  key={i} 
                  onClick={() => setActive(i)} 
                  className={`relative flex flex-col items-center group transition-all duration-500 ${i <= active ? "opacity-100" : "opacity-40"}`} 
                  data-testid={`process-step-${i}`}
                >
                  <div className={`w-16 h-16 flex items-center justify-center text-xl font-bold font-mono mb-4 transition-all duration-500 shadow-lg ${
                    i === active 
                      ? "bg-gradient-to-br from-forest to-emerald-600 text-white scale-110 shadow-forest/30" 
                      : i < active 
                        ? "bg-forest text-white" 
                        : "bg-white text-forest border-2 border-slate-200"
                  }`}>
                    {step.number}
                  </div>
                  <span className={`text-sm font-semibold font-sans transition-colors duration-300 ${i === active ? "text-forest" : "text-slate-400"}`}>
                    {step.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Content Area */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-navy via-navy/95 to-forest/20 shadow-2xl w-full h-[380px] flex items-center justify-center relative overflow-hidden group">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                </div>
                
                <div className="text-white text-center p-8 relative z-10">
                  <span className="text-8xl font-bold opacity-20 font-mono block mb-4">{steps[active].number}</span>
                  <p className="text-3xl font-serif tracking-tight">{steps[active].title}</p>
                </div>
                
                {/* Animated corner accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-coir/30 to-transparent" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-forest/30 to-transparent" />
              </div>
              
              {/* Floating badge */}
              <div className="absolute -top-4 -left-4 bg-gradient-to-br from-coir to-amber-500 p-4 shadow-xl z-10">
                <span className="text-3xl font-bold text-white font-mono">{steps[active].number}</span>
              </div>
            </div>
            
            {/* Text Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-forest/10 px-4 py-2 mb-6">
                <Check className="w-4 h-4 text-forest" />
                <span className="text-forest font-semibold text-sm font-sans">{steps[active].highlight}</span>
              </div>
              
              <h3 className="text-3xl font-serif text-navy mb-4 tracking-tight">{steps[active].title}</h3>
              <p className="text-slate-500 text-lg leading-relaxed mb-8 font-sans">{steps[active].desc}</p>
              
              {/* Navigation */}
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setActive(Math.max(0, active - 1))} 
                  disabled={active === 0} 
                  className="px-5 py-2.5 border-2 border-forest text-forest hover:bg-forest hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed font-sans text-sm font-semibold flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
                <button 
                  onClick={() => setActive(Math.min(steps.length - 1, active + 1))} 
                  disabled={active === steps.length - 1} 
                  className="px-5 py-2.5 bg-forest text-white hover:bg-forest/90 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 font-sans text-sm font-semibold"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Process Cards */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, i) => (
            <div 
              key={i} 
              className={`bg-white shadow-lg overflow-hidden transition-all duration-500 hover:shadow-xl ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} 
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="relative h-32 bg-gradient-to-br from-navy to-forest/30">
                <div className="absolute top-4 left-4 w-14 h-14 bg-gradient-to-br from-coir to-amber-500 flex items-center justify-center shadow-lg">
                  <span className="text-xl font-bold text-white font-mono">{step.number}</span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <span className="text-white/60 text-xs font-sans uppercase tracking-wider">{step.highlight}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif text-navy mb-3 tracking-tight">{step.title}</h3>
                <p className="text-slate-500 text-sm font-sans leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
